import React from 'react';
import { LayoutSkeleton } from './SidebarSkeleton';

// Enhanced Inventory Dashboard Skeleton
export const InventoryDashboardSkeleton: React.FC = () => {
  return (
    <LayoutSkeleton>
      <div className="min-h-screen bg-gray-50">
        {/* Header Section */}
        <div className="bg-white shadow-sm border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-gray-300 rounded-xl animate-pulse"></div>
                <div>
                  <div className="w-64 h-8 bg-gray-300 rounded animate-pulse mb-2"></div>
                  <div className="w-96 h-4 bg-gray-300 rounded animate-pulse"></div>
                </div>
              </div>
              
              <div className="w-32 h-12 bg-gray-300 rounded-lg animate-pulse"></div>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {Array.from({ length: 4 }).map((_, index) => (
              <div key={index} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="w-24 h-4 bg-gray-300 rounded animate-pulse mb-2"></div>
                    <div className="w-16 h-8 bg-gray-300 rounded animate-pulse"></div>
                  </div>
                  <div className="w-12 h-12 bg-gray-300 rounded-lg animate-pulse"></div>
                </div>
              </div>
            ))}
          </div>

          {/* Action Buttons */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8">
            <div className="w-32 h-6 bg-gray-300 rounded animate-pulse mb-6"></div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {Array.from({ length: 6 }).map((_, index) => (
                <div key={index} className="p-4 bg-gray-100 rounded-lg animate-pulse">
                  <div className="w-8 h-8 bg-gray-300 rounded mx-auto mb-2"></div>
                  <div className="w-16 h-3 bg-gray-300 rounded mx-auto"></div>
                </div>
              ))}
            </div>
          </div>

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Low Stock Items */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200">
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="w-40 h-6 bg-gray-300 rounded animate-pulse"></div>
                  <div className="w-20 h-6 bg-gray-300 rounded-full animate-pulse"></div>
                </div>
              </div>
              <div className="p-6">
                <div className="space-y-4">
                  {Array.from({ length: 4 }).map((_, index) => (
                    <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200">
                      <div className="flex-1">
                        <div className="w-32 h-4 bg-gray-300 rounded animate-pulse mb-2"></div>
                        <div className="w-24 h-3 bg-gray-300 rounded animate-pulse"></div>
                      </div>
                      <div className="text-right">
                        <div className="w-16 h-4 bg-gray-300 rounded animate-pulse mb-1"></div>
                        <div className="w-20 h-3 bg-gray-300 rounded animate-pulse"></div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Recent Transactions */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200">
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="w-48 h-6 bg-gray-300 rounded animate-pulse"></div>
                  <div className="w-16 h-4 bg-gray-300 rounded animate-pulse"></div>
                </div>
              </div>
              <div className="p-6">
                <div className="space-y-4">
                  {Array.from({ length: 4 }).map((_, index) => (
                    <div key={index} className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 bg-gray-300 rounded-full animate-pulse"></div>
                        <div>
                          <div className="w-32 h-4 bg-gray-300 rounded animate-pulse mb-1"></div>
                          <div className="w-24 h-3 bg-gray-300 rounded animate-pulse"></div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="w-12 h-4 bg-gray-300 rounded animate-pulse mb-1"></div>
                        <div className="w-16 h-3 bg-gray-300 rounded animate-pulse"></div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Stock Addition Guide */}
          <div className="mt-8 bg-gray-50 rounded-xl border border-gray-200 p-6">
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0">
                <div className="w-10 h-10 bg-gray-300 rounded-lg animate-pulse"></div>
              </div>
              <div className="flex-1">
                <div className="w-64 h-6 bg-gray-300 rounded animate-pulse mb-4"></div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {Array.from({ length: 3 }).map((_, index) => (
                    <div key={index} className="bg-white rounded-lg p-4 border border-gray-200">
                      <div className="flex items-center space-x-2 mb-2">
                        <div className="w-4 h-4 bg-gray-300 rounded animate-pulse"></div>
                        <div className="w-24 h-4 bg-gray-300 rounded animate-pulse"></div>
                      </div>
                      <div className="w-full h-3 bg-gray-300 rounded animate-pulse mb-2"></div>
                      <div className="w-full h-8 bg-gray-300 rounded animate-pulse"></div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </LayoutSkeleton>
  );
};

// Inventory Items List Skeleton
export const InventoryItemsSkeleton: React.FC = () => {
  return (
    <LayoutSkeleton>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="w-48 h-8 bg-gray-300 rounded animate-pulse"></div>
          <div className="flex space-x-3">
            <div className="w-32 h-10 bg-gray-300 rounded-lg animate-pulse"></div>
            <div className="w-24 h-10 bg-gray-300 rounded-lg animate-pulse"></div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
          <div className="flex flex-wrap gap-4">
            {Array.from({ length: 4 }).map((_, index) => (
              <div key={index} className="w-32 h-10 bg-gray-300 rounded-lg animate-pulse"></div>
            ))}
          </div>
        </div>

        {/* Items Table */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="p-4 border-b border-gray-200">
            <div className="w-32 h-5 bg-gray-300 rounded animate-pulse"></div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  {Array.from({ length: 7 }).map((_, index) => (
                    <th key={index} className="px-6 py-3 text-left">
                      <div className="w-20 h-4 bg-gray-300 rounded animate-pulse"></div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {Array.from({ length: 8 }).map((_, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    {Array.from({ length: 7 }).map((_, colIndex) => (
                      <td key={colIndex} className="px-6 py-4">
                        <div className="w-20 h-4 bg-gray-300 rounded animate-pulse"></div>
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between">
          <div className="w-32 h-4 bg-gray-300 rounded animate-pulse"></div>
          <div className="flex space-x-2">
            {Array.from({ length: 5 }).map((_, index) => (
              <div key={index} className="w-8 h-8 bg-gray-300 rounded animate-pulse"></div>
            ))}
          </div>
        </div>
      </div>
    </LayoutSkeleton>
  );
};

// Add Stock Modal Skeleton
export const AddStockModalSkeleton: React.FC = () => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="w-48 h-6 bg-gray-300 rounded animate-pulse"></div>
            <div className="w-6 h-6 bg-gray-300 rounded animate-pulse"></div>
          </div>

          <div className="space-y-6">
            <div>
              <div className="w-24 h-4 bg-gray-300 rounded animate-pulse mb-2"></div>
              <div className="w-full h-12 bg-gray-300 rounded-lg animate-pulse"></div>
            </div>

            <div>
              <div className="w-32 h-4 bg-gray-300 rounded animate-pulse mb-2"></div>
              <div className="w-full h-12 bg-gray-300 rounded-lg animate-pulse"></div>
            </div>

            <div>
              <div className="w-28 h-4 bg-gray-300 rounded animate-pulse mb-2"></div>
              <div className="w-full h-20 bg-gray-300 rounded-lg animate-pulse"></div>
            </div>

            <div className="flex space-x-4 pt-4">
              <div className="flex-1 h-12 bg-gray-300 rounded-lg animate-pulse"></div>
              <div className="flex-1 h-12 bg-gray-300 rounded-lg animate-pulse"></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Purchase Orders Skeleton
export const PurchaseOrdersSkeleton: React.FC = () => {
  return (
    <LayoutSkeleton>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="w-48 h-8 bg-gray-300 rounded animate-pulse"></div>
          <div className="flex space-x-3">
            <div className="w-32 h-10 bg-gray-300 rounded-lg animate-pulse"></div>
            <div className="w-24 h-10 bg-gray-300 rounded-lg animate-pulse"></div>
          </div>
        </div>

        {/* Status Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {Array.from({ length: 4 }).map((_, index) => (
            <div key={index} className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 bg-gray-300 rounded-lg animate-pulse"></div>
                <div className="w-16 h-4 bg-gray-300 rounded animate-pulse"></div>
              </div>
              <div className="w-20 h-8 bg-gray-300 rounded animate-pulse mb-2"></div>
              <div className="w-32 h-3 bg-gray-300 rounded animate-pulse"></div>
            </div>
          ))}
        </div>

        {/* Orders Table */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="p-4 border-b border-gray-200">
            <div className="w-32 h-5 bg-gray-300 rounded animate-pulse"></div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  {Array.from({ length: 6 }).map((_, index) => (
                    <th key={index} className="px-6 py-3 text-left">
                      <div className="w-20 h-4 bg-gray-300 rounded animate-pulse"></div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {Array.from({ length: 6 }).map((_, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    {Array.from({ length: 6 }).map((_, colIndex) => (
                      <td key={colIndex} className="px-6 py-4">
                        <div className="w-20 h-4 bg-gray-300 rounded animate-pulse"></div>
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </LayoutSkeleton>
  );
};
